import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../utils/cn';

/* ─── Variant / Size literal types ─── */
type BadgeVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'orange' | 'indigo';
type BadgeSize = 'sm' | 'default' | 'lg';

/* ─── Dot color mapping ─── */
const dotColors: Record<BadgeVariant, string> = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
  neutral: 'bg-gray-400',
  orange: 'bg-orange-500',
  indigo: 'bg-indigo-500',
};

/* ─── Icon size mapping ─── */
const iconSizes: Record<BadgeSize, number> = {
  sm: 12,
  default: 14,
  lg: 16,
};

/* ─── CVA Variants ─── */
const statusBadgeVariants = cva(
  'inline-flex items-center rounded-full text-xs font-medium',
  {
    variants: {
      variant: {
        success: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        error: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
        info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        neutral: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
        orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
      },
      size: {
        sm: 'px-2 py-0.5 gap-1',
        default: 'px-2.5 py-0.5 gap-1.5',
        lg: 'px-3 py-1 gap-1.5',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      size: 'default',
    },
  }
);

/* ─── Props ─── */
export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  variant: NonNullable<VariantProps<typeof statusBadgeVariants>['variant']>;
  withDot?: boolean;
  icon?: LucideIcon;
}

/* ─── Component ─── */
const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ className, variant, size, withDot = false, icon: IconComp, children, ...props }, ref) => {
    const resolvedSize = size ?? 'default';

    return (
      <span
        ref={ref}
        className={cn(statusBadgeVariants({ variant, size }), className)}
        {...props}
      >
        {withDot && (
          <span
            className={cn('w-1.5 h-1.5 rounded-full shrink-0', dotColors[variant])}
            aria-hidden="true"
          />
        )}
        {IconComp && (
          <IconComp size={iconSizes[resolvedSize]} className="shrink-0" aria-hidden="true" />
        )}
        {children}
      </span>
    );
  }
);
StatusBadge.displayName = 'StatusBadge';

export { StatusBadge };
export default StatusBadge;
